import React, { memo, useState, useRef, useCallback, useEffect } from 'react';
import { useStyles, useAnalytics } from '@sravni/react-utils';
import { URLS } from '../../constants/urls.js';
import { MESSAGE_TYPE } from '../../enums/MessageTypes.js';
import { useDebounceCallback } from '../../hooks/useDebounceCallback.js';
import '../../enums/DeviceType.js';
import { MenuTypes } from '../../types/MenuTypes.js';
import { addParams } from '../../utils/addParams.js';
import { createMessage } from '../../utils/createMessage.js';
import { createOptionArray } from '../../utils/createOprionArray.js';
import { requestScript } from '../../utils/requestScript.js';
import SuggestList from '../SuggestList/index.js';
import { GoogleResultList } from './GoogleResultList.js';
import { MessageList } from './MessageList.js';
import { ScenarionInput } from './ScenarioInput.js';
import modules_72a695e8 from './styles/Scenario.module.less.js';

const Scenario = memo(({ data, query, searchServiceHost, closeScenario, startMessages = [], startNewScenario, queryInput, }) => {
    const [value, setValue] = useState('');
    const [inputSettings, setInputSettings] = useState(null);
    const [messages, setMessages] = useState(startMessages);
    const [menuActions, setMenuActions] = useState([]);
    const [position, setPosition] = useState('');
    const scrollRef = useRef(null);
    const cx = useStyles(modules_72a695e8);
    const [suggests, setSuggests] = useState([]);
    const { pushEvent } = useAnalytics();
    const sendAnalyticsEvent = (eventAction, eventLabel) => {
        pushEvent({
            event: 'mainEvent',
            eventCategory: 'Поиск',
            eventAction,
            eventLabel,
        });
    };
    const inputMode = inputSettings?.mode ?? 'default';
    const handleSuggestClick = useCallback(async (url, label, scenarioName) => {
        if (scenarioName) {
            setValue('');
            startNewScenario(label, scenarioName);
            sendAnalyticsEvent('Запуск сценария', `Запрос - ${queryInput || query}|Сценарий - ${query}`);
            return;
        }
        const resultUrl = addParams(url, query);
        window.open(resultUrl, '_blank');
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, startNewScenario, queryInput]);
    const resetData = useCallback(async () => {
        if (query) {
            sendAnalyticsEvent('Запуск сценария', `Запрос - ${queryInput || query}|Сценарий - ${query}`);
            const newMessages = [
                createMessage(query, MESSAGE_TYPE.MESSAGE, true, data.position),
                ...data.messages.data.map((msg) => createMessage(msg.content, null, false, data.position)),
            ];
            setMessages((prev) => [...prev, ...newMessages]);
            const options = data.options?.data;
            const newMenuActions = createOptionArray(options);
            setMenuActions(newMenuActions);
        }
        setPosition(data.position);
        setInputSettings(data.inputSettings ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, query, queryInput]);
    const onAction = useCallback((action, type, label) => {
        if (type === MenuTypes.Step) {
            sendAnalyticsEvent('Отправка информации в сценарий', `Запрос - ${value}|Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}|Тип ввода - Кнопка`);
        }
        else if (type === MenuTypes.MenuItem) {
            sendAnalyticsEvent('Отправка информации в сценарий', `Запрос - ${value}|Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}|Тип ввода - Ручной`);
        }
        if ([MenuTypes.Contact, MenuTypes.Redirect].includes(type)) {
            sendAnalyticsEvent('Конец сценария', `Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}`);
            window.open(action, '_blank');
            if (type === MenuTypes.Redirect) {
                setMenuActions([]);
                setInputSettings(null);
                setSuggests([]);
                sendAnalyticsEvent('Переход по ссылке из сценария', `Название - ${label}|Раздел - На сайте|Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}`);
                startNewScenario('', data.scenarioName);
            }
            return true;
        }
        if (type === MenuTypes.ToUseDesk) {
            closeScenario();
            sendAnalyticsEvent('Конец сценария', `Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}`);
            return true;
        }
        if (type === MenuTypes.Cancel) {
            setMenuActions([]);
            setInputSettings(null);
            setSuggests([]);
            sendAnalyticsEvent('Сброс сценария', `Запрос - ${value}|Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}|Тип ввода - Кнопка`);
            startNewScenario('', data.scenarioName);
            return true;
        }
        return false;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeScenario, startNewScenario, data.scenarioName, query, value]);
    const onMenuClick = useCallback(async (action, label, type, menuPosition) => {
        const isAction = onAction(action, type, label);
        if (menuPosition) {
            await requestScript(data, position, searchServiceHost, { action: 'menu' });
        }
        if (isAction) {
            return;
        }
        const { response } = await requestScript(data, menuPosition ?? position, searchServiceHost, { action });
        setValue('');
        if (response.type === MenuTypes.ToUseDesk) {
            closeScenario();
            sendAnalyticsEvent('Конец сценария', `Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}`);
            return;
        }
        const responseMessages = response.messages.data;
        const options = response.options?.data;
        const newMenuActions = createOptionArray(options);
        const newMessages = [
            createMessage(label, MESSAGE_TYPE.MESSAGE, true, response.position),
            ...responseMessages.map((msg) => createMessage(msg.content, null, false, response.position)),
        ];
        setMessages((prev) => [...prev, ...newMessages]);
        setMenuActions(newMenuActions);
        sendAnalyticsEvent('Просмотр шага сценария', `Сценарий - ${query}|Шаг - ${data.scenarioName}/${action}`);
        setInputSettings(response?.inputSettings || null);
        setPosition(response?.position || '');
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchServiceHost, position, closeScenario, onAction, query]);
    useEffect(() => {
        resetData();
    }, [resetData]);
    const onInputEnter = useCallback(async () => {
        if (!value)
            return;
        if (inputSettings?.inputType === 'scenario') {
            const { response } = await requestScript(data, position, searchServiceHost, { inputValue: value });
            const actionResult = onAction([response.data?.url, new URLSearchParams(response.data?.params).toString()].join('?'), response.data?.type);
            if (actionResult) {
                setValue('');
                return;
            }
        }
        setMessages((prev) => [
            ...prev,
            createMessage(value, MESSAGE_TYPE.MESSAGE, true, position),
            createMessage(React.createElement(GoogleResultList, { searchServiceHost: searchServiceHost, query: value }), MESSAGE_TYPE.COMPONENT, false, position),
        ]);
        setValue('');
    }, [inputSettings?.inputType, value, data, searchServiceHost, position, onAction]);
    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onInputEnter();
        }
    };
    const loadSuggest = useDebounceCallback(async (value) => {
        try {
            if (!value) {
                setSuggests([]);
                return;
            }
            // TODO: Нужно вынести уровень общения по сети в отдельный слой(создать класс для работы с данными который под капотом спрячет способ получения данных)
            const response = await fetch(`${new URL(URLS.SEARCH_REQUEST, searchServiceHost)}?query=${encodeURIComponent(value)}`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setSuggests(data.items);
        }
        catch {
            setSuggests([]);
        }
    }, 300);
    const onChangeValue = useCallback((newValue) => {
        setValue(newValue);
        if (!newValue) {
            setSuggests([]);
            return;
        }
        if (inputSettings?.inputType === 'search' || !inputSettings?.inputType) {
            loadSuggest(newValue);
        }
    }, [setValue, inputSettings, setSuggests, loadSuggest]);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
        }
    }, [messages.length, suggests, menuActions]);
    return (React.createElement("div", { className: cx('container'), ref: scrollRef },
        React.createElement("div", { className: cx('content') },
            React.createElement(MessageList, { messages: messages, onAction: (action, label, menuPosition) => onMenuClick(action, label, MenuTypes.MenuItem, menuPosition) }),
            React.createElement("div", { className: cx('footer') },
                React.createElement("div", { className: cx('menu') }, menuActions?.map((item) => (React.createElement("div", { onClick: () => {
                        onMenuClick(item.payload, item.label, item.type);
                    }, className: cx('menuAction'), key: item.payload }, item.label)))),
                inputMode === 'default' && value && React.createElement(SuggestList, { suggests: suggests, onClick: handleSuggestClick }),
                React.createElement(ScenarionInput, { value: value, onChange: onChangeValue, inputMode: inputMode, onKeyDown: onKeyDown })))));
}, (prevProps, nextProps) => prevProps.data === nextProps.data || (prevProps.data !== nextProps.data && prevProps.query === nextProps.query));

export { Scenario };
//# sourceMappingURL=Scenario.js.map
